import {
  MelcoModelsNotification,
  formatRelativeFromNow,
} from "melco-shared-logic";
import {
  Col,
  Row,
  DesignTokens,
  Paragraph,
  List,
  Button,
  DeleteIcon,
} from "melco-ui";
import styled from "styled-components";
import { NotificationStatusIcon } from "./NotificationStatusIcon";
import { NotificationTitle } from "./NotificationTitle";
import { notificationStatusToName } from "../../helper/notificationStatus";
import { useDeleteNotifications } from "../../hooks/notifications/useDeleteNotifications";
import { useConfirm } from "../../hooks/useConfirm";
import { NOTIFICATIONS_POPOVER_CONFIRM_DOM_ID } from "./NotificationsPopover";
import { NOTIFICATIONS_CACHE_KEY } from "../../hooks/notifications/useNotifications";
import isEmpty from "lodash-es/isEmpty";
import { useQueryClient } from "react-query";

const { spacings } = DesignTokens;

const ListItemInner = styled.div`
  width: 100%;
`;

const TitleRow = styled(Row).attrs({
  gutter: [spacings.middle, spacings.middle],
  align: "bottom",
})`
  margin-bottom: ${spacings.small}px;
`;

const ColRightAligned = styled(Col)`
  text-align: right;
`;

const StyledDate = styled(Paragraph).attrs({
  type: "secondary",
  size: "small",
})`
  && {
    margin: 0;
  }
`;

const StyledErrorMessage = styled(Paragraph).attrs({
  type: "danger",
  size: "small",
})`
  && {
    margin: 0;
  }
`;

export type NotificationListItemProps = {
  notification: MelcoModelsNotification;
};

export const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
}) => {
  const { status, message, desc, event_type, created_on, completed_on } =
    notification;

  const statusName = notificationStatusToName(status!);
  const deleteNotification = useDeleteNotifications();
  const queryClient = useQueryClient();

  const confirmDelete = useConfirm(
    async () => {
      //TODO: Handle case if notification.id doesnt exist
      if (notification.id) {
        const successfullyDeletedIds = await deleteNotification([
          notification.id,
        ]);

        if (!isEmpty(successfullyDeletedIds)) {
          queryClient.invalidateQueries(NOTIFICATIONS_CACHE_KEY);
        }
      }
    },
    {
      translationPrefix: "notifications.actions.delete",
      translationOptions: {
        count: 1,
      },
      getContainer: () =>
        document.getElementById(NOTIFICATIONS_POPOVER_CONFIRM_DOM_ID)!,
    }
  );

  return (
    <List.Item>
      <ListItemInner>
        <Row>
          <Col span={2}>
            <NotificationStatusIcon status={notification.status!} />
          </Col>
          <Col span={19}>
            <TitleRow>
              <Col span={12}>
                <NotificationTitle eventType={event_type!} />
              </Col>
              <ColRightAligned span={12}>
                <StyledDate>
                  {formatRelativeFromNow(completed_on ?? created_on)}
                </StyledDate>
              </ColRightAligned>
            </TitleRow>
            <Row>
              <Col>{desc}</Col>
            </Row>
            {statusName === "error" && message && (
              <Row>
                <Col>
                  <StyledErrorMessage>{message}</StyledErrorMessage>
                </Col>
              </Row>
            )}
          </Col>
          <ColRightAligned span={3}>
            <Button
              type="text"
              size="small"
              icon={<DeleteIcon />}
              onClick={() => {
                confirmDelete();
              }}
              data-cy-id="notification-delete"
            />
          </ColRightAligned>
        </Row>
      </ListItemInner>
    </List.Item>
  );
};
